import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  NgZone,
  OutputRefSubscription,
  createComponent,
  inject,
} from '@angular/core';
import { FtTouchpointContainerComponent } from './ft-touchpoint-container.component';
import { NativeTouchpointContainerLauncher } from './native-touchpoint-container.token';

/**
 * WS5 Phase 3 #3 — native replacement for the AngularJS `TouchpointContainerService`
 * (account-v2/.../v1-adapters/touchpoint-container/touchpointContainer.service.ts),
 * whose `insertElement()` `$compile`-mounted a `<ft-touchpoint-container-ng ng-if>`
 * string into `document.body` (the MOUNT of the already-native
 * `FtTouchpointContainerComponent` was itself `$compile`-driven, forcing AngularJS
 * to keep booting). This mounts the component the same way `StoryModalLauncherService`
 * does — `createComponent` + `appRef.attachView` + `document.body.appendChild` — with
 * the identical `show(campaignUuid, propertyUuid, touchpointName, dataType)`/`hide()`
 * API the trigger directive calls.
 *
 * PARITY INVARIANT (matched to v1 + the AngularJS service): a single instance is kept
 * open; `show()` while already open RE-BINDS the four inputs via `ref.setInput()`
 * WITHOUT recreating — exactly as v1's `show()` reassigned `$ftTouchpointContainer`
 * (the `ng-if` stayed truthy → no remount, only the `[campaign-uuid]`/`[touchpoint-name]`
 * bindings updated). That input change fires the component's `ngOnChanges` →
 * `initialize()` (re-fetch tab + reload the header for the new touchpoint), preserving
 * the component's existing header-loaded-once invariant (the header is reloaded per
 * `initialize()`, never on a tab switch). `hide()` destroys the instance (v1's `hide()`
 * nulled the scope var → `ng-if` false → `$destroy`).
 *
 * The component signals close via its `close` output (the `.ft-btn-close` button + the
 * "could not get touchpoint data" error path) — we subscribe and tear down, so the
 * component does NOT need a back-reference to this service (avoids a service↔component
 * import that would re-enter the barrel; see native-touchpoint-container.token.ts).
 */
@Injectable({ providedIn: 'root' })
export class NativeTouchpointContainerService implements NativeTouchpointContainerLauncher {
  private ref: ComponentRef<FtTouchpointContainerComponent> | null = null;
  private closeSub: OutputRefSubscription | null = null;

  private readonly appRef = inject(ApplicationRef);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly zone = inject(NgZone);

  show(campaignUuid: string, propertyUuid: string, touchpointName: string, dataType = 'messaging'): void {
    this.zone.run(() => {
      if (!this.ref) {
        const ref = createComponent(FtTouchpointContainerComponent, {
          environmentInjector: this.environmentInjector,
        });
        ref.setInput('campaignUuid', campaignUuid);
        ref.setInput('propertyUuid', propertyUuid);
        ref.setInput('touchpointName', touchpointName);
        ref.setInput('tabType', dataType);
        this.closeSub = ref.instance.close.subscribe(() => this.hide());
        this.appRef.attachView(ref.hostView);
        document.body.appendChild(ref.location.nativeElement);
        ref.changeDetectorRef.detectChanges();
        this.ref = ref;
      } else {
        // Re-bind onto the open modal (no remount) — see PARITY INVARIANT above.
        this.ref.setInput('campaignUuid', campaignUuid);
        this.ref.setInput('propertyUuid', propertyUuid);
        this.ref.setInput('touchpointName', touchpointName);
        this.ref.setInput('tabType', dataType);
        this.ref.changeDetectorRef.detectChanges();
      }
    });
  }

  hide(): void {
    if (!this.ref) return;
    const ref = this.ref;
    this.ref = null;
    this.zone.run(() => {
      this.closeSub?.unsubscribe();
      this.closeSub = null;
      this.appRef.detachView(ref.hostView);
      ref.destroy();
      ref.location.nativeElement.remove();
    });
  }
}
